import { LegendType } from 'recharts';
import type { CostVsBudgetChartResponse } from '../../../../api/financials-client/financials-client.type';
import { formatCurrency } from '../../../../helpers/financial/financial';
import type { ChartType, CostVersusOutputMeasures } from '../../../../store/financials-store/financials-store.type';
import {
  graphAmethyst,
  graphCambridgeBlue,
  graphChefchaouenBlue,
  graphLightRed,
  graphPacificCyan,
  graphPictonBlue,
  graphSandyBrown,
  graphTropicalIndigo,
} from '../../../../styles/design-tokens';
import type { DataPoint } from '../overview.type';

type Payload = { value?: any; id?: any; type?: string; color?: string; [key: string]: any };

const measureColorMap = {
  cost: graphTropicalIndigo,
  daily_burn: graphLightRed,
  cost_per_point: graphPictonBlue,
  cost_per_task: graphSandyBrown,
  committed_points: graphChefchaouenBlue,
  completed_points: graphCambridgeBlue,
  committed_tasks: graphAmethyst,
  completed_tasks: graphPacificCyan,
};

const measureNameMap = {
  cost: 'Cost',
  daily_burn: 'Daily Burn',
  cost_per_point: 'Cost Per Point',
  cost_per_task: 'Cost Per Task',
  committed_points: 'Committed Points',
  completed_points: 'Velocity',
  committed_tasks: 'Committed Tasks',
  completed_tasks: 'Throughput',
};

/**
 * Transforms the cost versus output data to a format that can be used by the chart.
 *
 * @param {CostVsBudgetChartResponse | undefined} data - The cost versus output data to transform.
 * @returns {DataPoint[]} The transformed cost versus output data.
 */
function transformCostVersusOutputData(data: CostVsBudgetChartResponse | undefined): DataPoint[] {
  if (!data || !data.chart_data) {
    return [];
  }

  return data.chart_data.reduce((dataOverTime: DataPoint[], dataPoint) => {
    const line = {
      date: new Date(dataPoint.year, dataPoint.month - 1, 1),
      committed_points: 0,
      committed_tasks: 0,
      completed_points: 0,
      completed_tasks: 0,
      cost: 0,
      cost_per_point: 0,
      cost_per_task: 0,
      daily_burn: 0,
    };
    dataPoint.projects.map((projectData) => {
      line.committed_points += projectData.committed_points;
      line.committed_tasks += projectData.committed_tasks;
      line.completed_points += projectData.completed_points;
      line.completed_tasks += projectData.completed_tasks;
      line.cost += projectData.cost;
    });
    line.cost_per_point = line.completed_points > 0 ? Math.round(line.cost / line.completed_points) : 0;
    line.cost_per_task = line.completed_tasks > 0 ? Math.round(line.cost / line.completed_tasks) : 0;
    dataOverTime.push(line);
    return dataOverTime;
  }, []);
}

/**
 * Builds trendLines for all numeric keys in the dataset using least squares regression.
 *
 * @param {DataPoint[]} data Array of DataPoint objects, each with a date and multiple metrics
 * @returns {DataPoint[]} The same data with added trendLine values (key_trend)
 */
function buildTrendLines(data: DataPoint[]): DataPoint[] {
  if (!data || data.length <= 1) {
    return data;
  }

  // Clone the data to avoid modifying the original
  const result = JSON.parse(JSON.stringify(data));

  // Get all numeric keys except 'date'
  const keys = Object.keys(data[0]).filter((key) => {
    return key !== 'date' && typeof data[0][key as keyof DataPoint] === 'number';
  });

  // Convert dates to numerical values (days since first date) for calculation
  const firstDate = new Date(data[0].date).getTime();
  const xValues = data.map((point) => (new Date(point.date).getTime() - firstDate) / (24 * 60 * 60 * 1000));

  // For each key, calculate and add the trendLine
  keys.forEach((key) => {
    // Get y values for current key
    const yValues = data.map((point) => point[key as keyof DataPoint] as number);

    // Calculate mean of x and y values
    const xMean = xValues.reduce((sum, x) => sum + x, 0) / xValues.length;
    const yMean = yValues.reduce((sum, y) => sum + y, 0) / yValues.length;

    // Calculate slope (m) and y-intercept (b) using least squares method
    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < data.length; i++) {
      const xDiff = xValues[i] - xMean;
      const yDiff = yValues[i] - yMean;
      numerator += xDiff * yDiff;
      denominator += xDiff * xDiff;
    }

    const slope = denominator !== 0 ? numerator / denominator : 0;
    const intercept = yMean - slope * xMean;

    // Generate trendLine values
    const trendLineKey = `${key}_trend`;
    for (let i = 0; i < result.length; i++) {
      result[i][trendLineKey] = Math.round(slope * xValues[i] + intercept);
    }
  });

  return result;
}

/**
 * Formats the average value label for a given measure.
 *
 * @param measure - The measure to format the average value label for.
 * @param {number | null} averageValue - The average value to format.
 * @returns {string} The formatted average value label.
 */
function formatAverageValueLabel(measure: keyof typeof measureNameMap, averageValue: number | null): string {
  if (averageValue === null) {
    return '--';
  }

  switch (measure) {
    case 'cost':
      return formatCurrency(averageValue);
    case 'daily_burn':
      return formatCurrency(averageValue);
    case 'cost_per_point':
      return formatCurrency(averageValue);
    case 'cost_per_task':
      return formatCurrency(averageValue);
    case 'committed_points':
      return `${averageValue} points`;
    case 'committed_tasks':
      return `${averageValue} tasks`;
    case 'completed_points':
      return `${averageValue} points`;
    case 'completed_tasks':
      return `${averageValue} tasks`;
    default:
      return averageValue.toString();
  }
}

/**
 * Gets the average value for a given measure from the cost versus output data.
 *
 * @param measure - The measure to get the average value for.
 * @param {CostVsBudgetChartResponse | undefined} data - The cost versus output data to get the average value from.
 * @returns {number | null} The average value for the given measure.
 */
function getAverageValue(
  measure: keyof typeof measureNameMap,
  data: CostVsBudgetChartResponse | undefined,
): number | null {
  if (!data) {
    return null;
  }

  switch (measure) {
    case 'cost':
      return data.average.cost;
    case 'daily_burn':
      return data.average.daily_burn;
    case 'cost_per_point':
      return data.average.cost_per_point;
    case 'cost_per_task':
      return data.average.cost_per_task;
    case 'committed_points':
      return data.average.points_committed;
    case 'committed_tasks':
      return data.average.tasks_committed;
    case 'completed_points':
      return data.average.points_completed;
    case 'completed_tasks':
      return data.average.tasks_completed;
    default:
      return null;
  }
}

/**
 * Builds the average value label for a given measure.
 *
 * @param measure - The measure to build the average value label for.
 * @param {CostVsBudgetChartResponse | undefined} data - The cost versus output data to build the average value label from.
 * @returns {string} The average value label for the given measure.
 */
function buildAverageValueLabel(
  measure: keyof typeof measureNameMap,
  data: CostVsBudgetChartResponse | undefined,
): string {
  const averageValue = getAverageValue(measure, data);
  return formatAverageValueLabel(measure, averageValue);
}

/**
 * Builds the legend for the cost versus output chart.
 *
 * @param measures - The measures to include in the legend.
 * @returns {Payload[]} The legend for the cost versus output chart.
 */
function buildChartLegend(measures: CostVersusOutputMeasures[] = [], chartType: ChartType): Payload[] {
  const payload: Payload[] = [];
  const type = chartType === 'line' ? ('line' as LegendType) : ('square' as LegendType);
  if (measures.includes('cost')) {
    payload.push({ id: 'cost', value: 'Cost', color: measureColorMap.cost, type });
  }
  if (measures.includes('daily_burn')) {
    payload.push({ id: 'daily_burn', value: 'Daily Burn', color: measureColorMap.daily_burn, type });
  }
  if (measures.includes('cost_per_point')) {
    payload.push({
      id: 'cost_per_point',
      value: 'Cost Per Point',
      color: measureColorMap.cost_per_point,
      type,
    });
  }
  if (measures.includes('cost_per_task')) {
    payload.push({ id: 'cost_per_task', value: 'Cost Per Task', color: measureColorMap.cost_per_task, type });
  }
  if (measures.includes('committed_points')) {
    payload.push({
      id: 'committed_points',
      value: 'Committed Points',
      color: measureColorMap.committed_points,
      type,
    });
  }
  if (measures.includes('completed_points')) {
    payload.push({ id: 'completed_points', value: 'Velocity', color: measureColorMap.completed_points, type });
  }
  if (measures.includes('committed_tasks')) {
    payload.push({
      id: 'committed_tasks',
      value: 'Committed Tasks',
      color: measureColorMap.committed_tasks,
      type,
    });
  }
  if (measures.includes('completed_tasks')) {
    payload.push({ id: 'completed_tasks', value: 'Throughput', color: measureColorMap.completed_tasks, type });
  }
  return payload;
}

/**
 * Formats a tooltip value based on the measure type.
 *
 * @param {string | undefined} rawValue - The raw value to be formatted
 * @param {keyof typeof measureNameMap} rawName - The name of the measure
 * @returns {string} The formatted value as a string
 */
function formatTooltipValue(rawValue: string | undefined, rawName: keyof typeof measureNameMap): string {
  if (rawValue === undefined) {
    return '--';
  }

  switch (rawName) {
    case 'cost':
      return `$${rawValue}`;
    case 'daily_burn':
      return `$${rawValue}`;
    case 'cost_per_point':
      return `$${rawValue}`;
    case 'cost_per_task':
      return `$${rawValue}`;
    case 'committed_points':
      return rawValue;
    case 'committed_tasks':
      return rawValue;
    case 'completed_points':
      return rawValue;
    case 'completed_tasks':
      return rawValue;
    default:
      return rawValue;
  }
}

export {
  buildAverageValueLabel,
  buildChartLegend,
  buildTrendLines,
  formatTooltipValue,
  getAverageValue,
  measureColorMap,
  measureNameMap,
  transformCostVersusOutputData,
};
